/**
 * Centralized UI messages, toast notifications, error strings, placeholders, and fallbacks for Admin app.
 */

export const MESSAGES = {
  common: {
    unnamed: 'Sans nom',
    untitled: 'Sans titre',
    notSpecified: 'Non renseigné',
    unknown: 'Inconnu',
    noDescription: 'Pas de description',
    none: 'Aucun',
    loading: 'Chargement…',
    default: 'Par défaut',
    draft: 'Brouillon',
    pending: 'En attente',
    all: 'Tous',
    search: 'Rechercher…',
    save: 'Enregistrer',
    cancel: 'Annuler',
    delete: 'Supprimer',
    edit: 'Modifier',
    create: 'Créer',
    back: 'Retour',
    confirm: 'Confirmer',
  },
  placeholders: {
    categoryName: 'Nom de la catégorie',
    productTitle: 'Titre du produit',
    searchProducts: 'Rechercher un produit…',
    searchOrders: 'Rechercher une commande…',
    searchCustomers: 'Rechercher un client…',
    email: 'email@exemple.fr',
    sku: 'Ex: MOB-LIT-140',
    price: '0.00 €',
  },
  validation: {
    firstNameRequired: 'Le prénom est requis',
    lastNameRequired: 'Le nom est requis',
    invalidEmail: 'Adresse email invalide',
    passwordRequired: 'Le mot de passe est requis',
    passwordMinLength: 'Le mot de passe doit contenir au moins 8 caractères',
    confirmPasswordMatch: 'Veuillez confirmer le nouveau mot de passe',
    passwordMismatch: 'Les mots de passe ne correspondent pas',
    categoryNameRequired: 'Le nom de la catégorie est requis',
    productTitleRequired: 'Le titre du produit est requis',
    variantTitleRequired: 'Le titre de la variante est requis',
    validAmountRequired: 'Veuillez entrer une valeur positive valide',
    trackingNumberRequired: 'Numéro requis',
    requiredFieldsMissing: 'Veuillez remplir tous les champs obligatoires',
    variantTitleOrOptionRequired: 'Un titre ou une option (couleur/dimensions) est requis',
  },
  toast: {
    success: {
      login: 'Connexion réussie',
      welcome: 'Bienvenue !',
      productCreated: 'Produit créé avec succès',
      productUpdated: 'Produit mis à jour',
      productDeleted: 'Produit supprimé',
      categoryDeleted: 'Catégorie supprimée',
      categoryUpdated: 'Catégories mises à jour',
      customerDeleted: 'Client supprimé',
      stockUpdated: 'Stock mis à jour',
      promotionCreated: 'Promotion créée',
      promotionUpdated: 'Promotion mise à jour',
      promotionDeleted: 'Promotion supprimée',
      imageUploaded: 'Image téléversée',
      imagesUploaded: 'Image(s) ajoutée(s)',
      imageDeleted: 'Image supprimée',
      thumbnailSet: 'Vignette définie',
      variantCreated: 'Variante créée',
      variantDeleted: 'Variante supprimée',
      variantAdded: 'Variante ajoutée',
      priceAdded: 'Prix ajouté',
      priceDeleted: 'Prix supprimé',
      salesChannelsUpdated: 'Canaux de vente mis à jour',
      colorsUpdated: 'Couleurs mises à jour',
      texturesUploaded: 'Texture téléversée',
      orderCancelled: 'Commande annulée',
      fulfillmentCancelled: 'Préparation annulée',
      orderEdited: 'Commande modifiée',
      paymentCaptured: 'Paiement capturé',
      fulfillmentCreated: 'Expédition créée',
      shipmentCreated: 'Colis expédié',
      exchangeCreated: 'Échange créé',
      refundIssued: 'Remboursement émis',
      returnRequested: 'Retour demandé',
      requestRefused: 'Demande refusée',
      elementDeleted: 'Élément supprimé',
      rateDeleted: 'Taux supprimé',
      locationCreated: 'Emplacement de stock créé',
      locationUpdated: 'Emplacement de stock mis à jour',
      locationDeleted: 'Emplacement de stock supprimé',
      profileUpdated: 'Informations mises à jour',
      passwordChanged: 'Mot de passe modifié',
      addressAdded: 'Adresse ajoutée',
      addressUpdated: 'Adresse mise à jour',
      addressDeleted: 'Adresse supprimée',
      draftOrderCreated: 'Brouillon de commande créé avec succès',
      orderConfirmed: 'Commande confirmée avec succès',
      paymentLinkCopied: 'Lien de paiement copié dans le presse-papier !',
      paymentLinkGenerated: 'Lien de paiement généré',
      draftDeleted: 'Brouillon supprimé',
      variantsBatchUpdated: 'Variantes mises à jour en lot',
      lookProductsUpdated: 'Produits associés du Look mis à jour',
      reviewModerated: 'Avis modéré',
      reviewApproved: 'Avis approuvé',
      reviewRejected: 'Avis rejeté',
      reviewDeleted: 'Avis supprimé',
      claimCreated: 'Réclamation créée',
      returnReasonAdded: 'Motif de retour ajouté',
      returnReasonUpdated: 'Motif de retour mis à jour',
      returnReasonDeleted: 'Motif de retour supprimé',
      paymentProvidersUpdated: 'Moyens de paiement mis à jour',
      shippingOptionCreated: 'Option créée',
      shippingOptionUpdated: 'Option mise à jour',
      shippingOptionDeleted: 'Option supprimée',
      shippingProfileCreated: 'Profil créé',
      shippingProfileDeleted: 'Profil supprimé',
      shippingZoneCreated: 'Zone créée',
      shippingZoneDeleted: 'Zone supprimée',
      shippingLocationCreated: 'Emplacement créé',
      shippingLocationUpdated: 'Emplacement mis à jour',
      shippingLocationDeleted: 'Emplacement supprimé',
      taxRegionCreated: 'Région de taxe créée',
      taxRegionUpdated: 'Région de taxe mise à jour',
      taxRegionDeleted: 'Région de taxe supprimée',
      taxRateCreated: 'Taux de taxe créé',
      taxRateUpdated: 'Taux de taxe mis à jour',
      taxRateDeleted: 'Taux de taxe supprimé',
      csvExportDone: 'Export CSV terminé',
    },
    error: {
      unknown: 'Une erreur inattendue est survenue',
      updateFailed: 'Erreur lors de la mise à jour',
      deleteFailed: 'Erreur lors de la suppression',
      createFailed: 'Erreur lors de la création',
      nameRequired: 'Le nom est requis',
      titleRequired: 'Le titre est requis',
      invalidAmount: 'Montant invalide',
      noItemsSelected: 'Sélectionnez au moins un article',
      loginFailed: 'Échec de la connexion',
      networkError: 'Erreur de connexion réseau',
      categoryNameRequired: 'Le nom de la catégorie est requis',
      productTitleRequired: 'Le titre du produit est requis',
      variantTitleRequired: 'Le titre de la variante est requis',
      validPositiveValueRequired: 'Veuillez entrer une valeur positive valide',
      claimError: 'Erreur de réclamation',
      exchangeError: "Erreur d'échange",
      fulfillmentError: "Erreur d'expédition",
      shipmentError: "Erreur d'expédition",
      orderEditError: 'Erreur de modification',
      returnError: 'Erreur de retour',
      receiveError: 'Erreur de réception',
      cancelError: "Erreur d'annulation",
      captureError: 'Erreur de capture',
      moderationError: 'Erreur de modération',
      numberRequired: 'Numéro requis',
      noChanges: 'Aucune modification',
      refundError: 'Erreur de remboursement',
      csvExportFailed: 'Échec de l’export',
      validationError: 'Erreur lors de la validation',
      variantColorAndImageRequired: 'Veuillez saisir une couleur et choisir au moins une photo',
      variantNotFoundForColor: 'Aucune variante trouvée pour cette couleur',
      inventoryItemMissing: "Ligne d'inventaire manquante",
      inventoryItemMissingDetail:
        "Aucune ligne d'inventaire n'est associée à cette variante. Allez dans Inventaire pour l'initialiser.",
    },
  },
  errors: {
    appError: "L'application a rencontré un problème inattendu.",
    idMissing: 'ID manquant',
    reasonMissing: 'ID de motif manquant',
  },
} as const;

export function getErrorMessage(
  error: unknown,
  fallback: string = MESSAGES.toast.error.unknown
): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim()) {
    return error;
  }
  return fallback;
}
